import { Card, ContentLoader, Progress, Steps, Text } from "../../components";
import { useParams } from "react-router-dom";
import { OrderSummary } from "../../layouts";
import { useEffect, useLayoutEffect, useState } from "react";
import { Order } from "../../types/item";
import Enums, { OrderStatus, OrderType } from "../../types/enums";
import { useApp, useAuth, useNavigate, useTheme } from "../../hooks";
import APIs from "../../apis";
import { Result } from "antd";

const Skeleton = () => (
  <>
    <Card className="m-20-b">
      <ContentLoader
        uniqueKey="track-status"
        viewBox="0 0 640 72"
        style={{ width: "100%", height: "7.2rem" }}
      >
        <rect x="0" y="20" rx="8" ry="8" width="640" height="14" />
        <rect x="250" y="46" rx="4" ry="4" width="140" height="16" />
      </ContentLoader>
    </Card>
    <Card>
      <ContentLoader
        uniqueKey="track-summary"
        viewBox="0 0 640 300"
        style={{ width: "100%", height: "30rem" }}
      >
        <rect x="0" y="8" rx="6" ry="6" width="36" height="36" />
        <rect x="52" y="16" rx="4" ry="4" width="180" height="16" />
        <rect x="540" y="16" rx="4" ry="4" width="80" height="16" />
        <rect x="0" y="64" rx="6" ry="6" width="36" height="36" />
        <rect x="52" y="72" rx="4" ry="4" width="220" height="16" />
        <rect x="540" y="72" rx="4" ry="4" width="80" height="16" />
        <rect x="0" y="120" rx="6" ry="6" width="36" height="36" />
        <rect x="52" y="128" rx="4" ry="4" width="150" height="16" />
        <rect x="540" y="128" rx="4" ry="4" width="80" height="16" />
        <rect x="0" y="190" rx="4" ry="4" width="100" height="12" />
        <rect x="540" y="190" rx="4" ry="4" width="80" height="12" />
        <rect x="0" y="220" rx="4" ry="4" width="140" height="12" />
        <rect x="540" y="220" rx="4" ry="4" width="80" height="12" />
        <rect x="0" y="258" rx="4" ry="4" width="120" height="18" />
        <rect x="500" y="254" rx="4" ry="4" width="120" height="22" />
      </ContentLoader>
    </Card>
  </>
);

const TrackOrder = () => {
  const [order, setOrder] = useState<Order>();
  const [loading, setLoading] = useState(true);
  const { id = "" } = useParams();
  const { navigate, setOptions } = useNavigate();
  const { message } = useApp();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();

  useLayoutEffect(() => {
    if (order?.orderNo) setOptions({ title: `Order #${order.orderNo}` });
  }, [order?.orderNo, setOptions]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const load = async () => {
      try {
        const next = await APIs.orders.order(id);
        if (active) setOrder(next);
      } catch (error) {
        message.error(`Order not found`);
        navigate(isAuthenticated ? "/orders" : "/");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading || !order) return <Skeleton />;
  const isPickup = order.orderType === OrderType.pickup;
  const isCancelled = order.status === OrderStatus.cancelled;
  const status = Enums.details(order.status);
  const items = [
    { title: "Pending" },
    { title: "In-Process" },
    { title: isPickup ? "Ready" : "Dispatched" },
    { title: "Complete" },
  ];
  const current = Math.min(status?.step ?? 0, items.length - 1);
  return (
    <>
      <Card className="m-20-b">
        {isCancelled ? (
          <Result
            status="error"
            className="m-0 p-0"
            title={<Text level={3}>Order Cancelled</Text>}
            subTitle={
              <Text level={5} type="secondary">
                Reason: {order.cancelReason}
              </Text>
            }
          />
        ) : (
          <>
            <Steps
              responsive={false}
              direction="horizontal"
              labelPlacement="vertical"
              current={current}
              className="d-none-mobile"
              items={items}
            />
            <div className="d-none d-flex-mobile w-100 flex-col">
              <Progress
                steps={items.length}
                showInfo={false}
                size={16}
                className="track-order-progress"
                strokeColor={theme.colors.primary}
                percent={((current + 1) / items.length) * 100}
                status={"active"}
              />
              <Text
                align="center"
                weight={700}
                size="2.2rem"
                lineHeight="2.6rem"
                className="m-16-t"
                type={undefined}
                children={items[current].title}
              />
            </div>
          </>
        )}
      </Card>
      <Card classNames={{ body: "p-0" }}>
        <OrderSummary {...order} />
      </Card>
    </>
  );
};

export default TrackOrder;
